import * as React from 'react';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';

import type {QuoteTimes} from '../../../../../../ui/lead/sales-documents/shared';

import {colors} from '../../colors/colors';
import {
    AcceptedIcon,
    ArchiveIcon,
    MinusCircleIcon,
    DocumentReadyIcon,
    DraftIcon,
    SendIcon,
    NavIconSalesIcon,
    TimesCircleIcon,
    WarningIcon,
    ClockIcon,
} from '../../icon';
import {getSalesDocumentStatusTime} from '../../lead-sidebar/sales-documents/helpers';
import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';

import {Chevron} from './chevron';
import type {Props as BackgroundComponentProps} from './chevron';
import {Rectangle} from './rectangle';
import type {StatusLabelVariant} from './status-label';

type ButtonConfig = {
    variant: StatusLabelVariant;
    onAdvance: () => void;
    disabled: boolean;
};

export function buildButtonConfig(
    quoteStatus: QuoteDisplayStatus,
    handleUpdateStatus: (status: QuoteDisplayStatus) => void
): ButtonConfig[] {
    switch (quoteStatus) {
        case 'DRAFT':
        case 'PUBLISHED':
        case 'SENT':
        case 'ACCEPTED': {
            return [
                {
                    variant: 'DRAFT',
                    onAdvance: () => {},
                    disabled: getButtonDisabledFromStatus('DRAFT', quoteStatus),
                },
                {
                    variant: 'PUBLISHED',
                    onAdvance: () => handleUpdateStatus('PUBLISHED'),
                    disabled: getButtonDisabledFromStatus('PUBLISHED', quoteStatus),
                },
                {
                    variant: 'SENT',
                    onAdvance: () => handleUpdateStatus('SENT'),
                    disabled: getButtonDisabledFromStatus('SENT', quoteStatus),
                },
                {
                    variant: 'ACCEPTED',
                    onAdvance: () => handleUpdateStatus('ACCEPTED'),
                    disabled: getButtonDisabledFromStatus('ACCEPTED', quoteStatus),
                },
            ];
        }
        case 'ARCHIVED':
            return [
                {
                    variant: 'ARCHIVED',
                    onAdvance: () => {},
                    disabled: true,
                },
            ];
        case 'REVOKED':
            return [
                {
                    variant: 'REVOKED',
                    onAdvance: () => {},
                    disabled: true,
                },
            ];
        case 'EXPIRED':
            return [
                {
                    variant: 'EXPIRED',
                    onAdvance: () => {},
                    disabled: true,
                },
            ];
        default:
            return [];
    }
}

/**
 * Returns true if the button variant should be disabled based on the status of
 * the quote. This Prevents the user from advancing the quote to a status that
 * is not allowed.
 */
function getButtonDisabledFromStatus(
    variant: StatusLabelVariant,
    status: QuoteDisplayStatus
): boolean {
    switch (variant) {
        case 'DRAFT':
            return true;
        case 'PUBLISHED':
            return status !== 'DRAFT';
        case 'SENT':
            return status !== 'PUBLISHED';
        case 'ACCEPTED':
            return status !== 'SENT';
        default:
            return false;
    }
}

/**
 * Returns true if the button variant should be active based on the status of
 * the quote. This is purely visual and does not affect the functionality of the
 * button.
 */
export function getActiveStatus(variant: StatusLabelVariant, status: QuoteDisplayStatus): boolean {
    switch (variant) {
        case 'DRAFT':
            return true;
        case 'PUBLISHED':
            return status === 'PUBLISHED' || status === 'SENT' || status === 'ACCEPTED';
        case 'SENT':
            return status === 'SENT' || status === 'ACCEPTED';
        case 'ACCEPTED':
            return status === 'ACCEPTED';
        default:
            return true;
    }
}

function getIconColor(
    quoteStatus: QuoteDisplayStatus,
    variant: StatusLabelVariant,
    isActive: boolean
) {
    if (quoteStatus === 'ACCEPTED' && variant === 'ACCEPTED') {
        return colors.lead;
    }
    if (quoteStatus === 'EXPIRED') {
        return colors.roseDk;
    }
    if (quoteStatus === 'REVOKED' || quoteStatus === 'ARCHIVED') {
        return colors.greyLt;
    }
    if (isActive) {
        return colors.person;
    }

    return colors.greyLt;
}

export const getStatusLabelIcon = (
    variant: StatusLabelVariant,
    isActive: boolean,
    type: SalesDocumentType,
    quoteStatus: QuoteDisplayStatus
) => {
    const iconColor = getIconColor(quoteStatus, variant, isActive);

    switch (variant) {
        case 'PUBLISHED':
            // @ts-expect-error
            return <DocumentReadyIcon alignCenter={true} size={17} color={iconColor} />;
        case 'SENT':
            return <SendIcon alignCenter={true} size={15} color={iconColor} />;
        case 'ACCEPTED':
            return type === 'Quote' ? (
                // @ts-expect-error
                <AcceptedIcon alignCenter={true} size={17} color={iconColor} />
            ) : (
                // @ts-expect-error
                <NavIconSalesIcon alignCenter={true} size={17} color={iconColor} />
            );
        case 'ARCHIVED':
            return <ArchiveIcon alignCenter={true} size={17} color={iconColor} />;
        case 'REVOKED':
            return type === 'Quote' ? (
                <MinusCircleIcon alignCenter={true} size={17} style={{color: iconColor}} />
            ) : (
                <TimesCircleIcon alignCenter={true} size={17} style={{color: iconColor}} />
            );
        case 'DRAFT':
            // @ts-expect-error
            return <DraftIcon alignCenter={true} size={17} color={iconColor} wrapWithDiv={false} />;
        case 'EXPIRED':
            return type === 'Quote' ? (
                <ClockIcon alignCenter={true} size={17} style={{color: iconColor}} />
            ) : (
                // @ts-expect-error
                <WarningIcon alignCenter={true} size={17} fill={iconColor} />
            );
        default:
            // @ts-expect-error
            return <DraftIcon alignCenter={true} size={17} color={iconColor} wrapWithDiv={false} />;
    }
};

export function getBackgroundComponent(
    variant: StatusLabelVariant
): React.ComponentType<BackgroundComponentProps> {
    switch (variant) {
        case 'DRAFT':
        case 'PUBLISHED':
        case 'SENT':
            return Chevron;
        default:
            return Rectangle;
    }
}

export type DialogCopy = {
    question: string;
    primaryText: string;
    secondaryText: string;
};

const TO_READY_DIALOGUE_COPY: DialogCopy = {
    question:
        'This {documentType} will be locked in and available to send to your customers. Are you sure?',
    primaryText: 'Mark as ready',
    secondaryText: 'Keep as draft',
};

const TO_SENT_DIALOGUE_COPY: DialogCopy = {
    question: 'Mark this {documentType} as sent to your lead?',
    primaryText: 'Mark as sent',
    secondaryText: 'Keep as ready',
};

const TO_ACCEPTED_DIALOGUE_COPY: DialogCopy = {
    question: 'Finalize this quote by marking it as accepted by your customer?',
    primaryText: 'Mark accepted',
    secondaryText: 'Keep as sent',
};

const DEFAULT_DIALOGUE_COPY: DialogCopy = {
    question: 'Advance to this status?',
    primaryText: 'Advance',
    secondaryText: 'Cancel',
};

function fillDialogCopyTemplate(copy: DialogCopy, type: SalesDocumentType): DialogCopy {
    return {
        question: copy.question.replace('{documentType}', type.toLowerCase()),
        primaryText: copy.primaryText,
        secondaryText: copy.secondaryText,
    };
}

export function getDialogCopy(status: QuoteDisplayStatus, type: SalesDocumentType): DialogCopy {
    switch (status) {
        case 'DRAFT':
            return fillDialogCopyTemplate(TO_READY_DIALOGUE_COPY, type);
        case 'PUBLISHED':
            return fillDialogCopyTemplate(TO_SENT_DIALOGUE_COPY, type);
        case 'SENT':
            return TO_ACCEPTED_DIALOGUE_COPY;
        case 'ACCEPTED':
            return DEFAULT_DIALOGUE_COPY;
        default:
            return DEFAULT_DIALOGUE_COPY;
    }
}

export function getLabelTooltip(
    variant: StatusLabelVariant,
    status: QuoteDisplayStatus,
    quoteTimes: QuoteTimes,
    featureGated: boolean | null | undefined,
    type: SalesDocumentType
): string {
    if (featureGated && status === 'DRAFT' && variant !== 'DRAFT') {
        return '';
    }

    return getSalesDocumentStatusTime(variant, type, quoteTimes);
}
